import { useConfig } from '@/modules/config/hooks/useConfig'
import { useConfigMutation } from '@/modules/config/hooks/useConfigMutation'
import { ExchangeSettingsForm } from '@/modules/config/components/ExchangeSettingsForm'
import { CategoriesForm } from '@/modules/config/components/CategoriesForm'
import { EmailsForm } from '@/modules/config/components/EmailsForm'
import { ProcessDailyButton } from '@/modules/config/components/ProcessDailyButton'
import { SentryTestButton } from '@/modules/config/components/SentryTestButton'
import { ErrorState } from '@/shared/components/ui/atoms/ErrorState'

export function ConfigPage() {
  const { data, isLoading, error, refetch } = useConfig()
  const {
    updateExchangeSettings,
    updateCategories,
    updateEmails,
    isSubmitting,
    error: mutationError,
    success,
  } = useConfigMutation()

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-2xl">
        <div className="animate-pulse h-8 w-48 bg-gray-100 rounded" />
        <div className="animate-pulse h-64 bg-gray-100 rounded-lg" />
        <div className="animate-pulse h-48 bg-gray-100 rounded-lg" />
        <div className="animate-pulse h-32 bg-gray-100 rounded-lg" />
      </div>
    )
  }

  if (error) {
    return <ErrorState message={error} onRetry={refetch} />
  }

  if (!data) return null

  const emailsString = Object.values(data.list_notification_emails).join(', ')

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Configuración</h1>

      {/* Feedback messages */}
      {mutationError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {mutationError}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {success}
        </div>
      )}

      {/* Section 1: Exchange Settings */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Configuración de Puntos</h2>
        <ExchangeSettingsForm
          defaultValues={{
            min_exchange_points: data.min_exchange_points,
            value_point_to_CLP: data.value_point_to_CLP,
            value_CLP_to_point: data.value_CLP_to_point,
            months_expiration_points: data.months_expiration_points,
          }}
          onSubmit={updateExchangeSettings}
          isSubmitting={isSubmitting}
        />
      </div>

      {/* Section 2: Categories */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Categorías</h2>
        <CategoriesForm
          defaultValues={{
            oro: data.categories?.values?.oro ?? 0,
            marmol: data.categories?.values?.marmol ?? 0,
            bronce: data.categories?.values?.bronce ?? 0,
          }}
          onSubmit={updateCategories}
          isSubmitting={isSubmitting}
        />
      </div>

      {/* Section 3: Notification Emails */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Emails de notificación</h2>
        <EmailsForm
          defaultValue={emailsString}
          onSubmit={updateEmails}
          isSubmitting={isSubmitting}
        />
      </div>

      {/* Section 4: Process Daily (CRON) */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Proceso diario de beneficios</h2>
        <ProcessDailyButton />
      </div>

      {/* Section 5: Sentry Test */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Sentry Error Tracking</h2>
        <SentryTestButton />
      </div>
    </div>
  )
}
